import { Link } from "react-router-dom";
import { Play, ArrowRight, FileText, CheckCircle2, Disc3, Clock, Music4, Radio, Disc } from "lucide-react";
import { usePlayer } from "../context/PlayerContext";
import { Equalizer as Eq } from "../components/Player";
import { useLanguage } from "../context/LanguageContext";

const HERO_IMG =
  "https://images.unsplash.com/photo-1708871171307-4e2e94dd7b9d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwyfHxtdXNpYyUyMHJlY29yZGluZyUyMHN0dWRpbyUyMG1pY3JvcGhvbmUlMjBoZWFkcGhvbmVzJTIwdmlueWwlMjBhbGJ1bSUyMHNvdW5kJTIwbWl4aW5nJTIwY29uc29sZXxlbnwwfHx8fDE3OTE1MTk0ODh8MA&ixlib=rb-4.1.0&q=85";

const STEPS = [
  { icon: FileText, title: "1. Tell your story", text: "Fill in the order form: occasion, who the song is about, key facts and your preferred style." },
  { icon: Music4, title: "2. We write the lyrics", text: "Your personal lyric is created first — nothing is produced before you have seen it." },
  { icon: CheckCircle2, title: "3. You confirm & pay", text: "Approve the lyric, then pay by card or Swiss QR bank transfer. Production starts after verified payment." },
  { icon: Disc3, title: "4. Production starts", text: "As soon as payment arrives, we produce. Private packages are delivered within 24 hours." },
];

const PACKAGES = [
  {
    icon: Music4,
    name: "Private Custom Songs",
    price: "CHF 300",
    tag: "Best Value for Personal Gifts",
    points: ["6 songs in 3 different music styles", "Delivered within 24h after lyric confirmation & payment", "Studio mixing & mastering included"],
    to: "/order?package=private",
    cta: "Start a Private Order",
    testid: "home-package-private",
  },
  {
    icon: Clock,
    name: "Private Events",
    price: "from CHF 150",
    tag: "Birthdays & Weddings",
    points: ["2 versions of the same personalized song", "Male or female studio vocalist", "Guided, effortless process"],
    to: "/order?package=event",
    cta: "Plan an Event Song",
    testid: "home-package-event",
  },
  {
    icon: Radio,
    name: "Business & Jingles",
    price: "By agreement",
    tag: "Radio Advertising",
    points: ["Radio & TV advertising jingles", "Optional concept & idea development", "Broadcast licensing included"],
    to: "/business",
    cta: "For Your Brand",
    testid: "home-package-business",
  },
  {
    icon: Disc,
    name: "Album Production",
    price: "By agreement",
    tag: "Full Productions",
    points: ["Arrangement, recording & mastering", "Rights & distribution options", "Spotify, Apple Music & Vinyl"],
    to: "/album",
    cta: "Produce an Album",
    testid: "home-package-album",
  },
];

export const HomeHero = () => {
  const { t } = useLanguage();
  const { playTrack, isPlaying } = usePlayer();

  return (
      <section className="mx-auto max-w-6xl px-4 pb-20 pt-16 sm:px-6 lg:px-8 lg:pt-24">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div className="fade-up">
            <p className="eyebrow">Evi-Studio · by Evinet.group</p>
            <h1 className="mt-4 font-display text-4xl font-extrabold tracking-tight text-slate-50 sm:text-5xl lg:text-6xl">
              {t("Your story.")}<br />{t("Your song.")}<br />
              <span className="text-[#E5A93C]">{t("Studio-produced.")}</span>
            </h1>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-slate-400 sm:text-lg">
              {t("Order tailor-made custom songs, jingles and album productions. We write the lyric first, you confirm it, pay online — and production starts immediately. Private packages delivered within 24 hours.")}
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/order" data-testid="home-hero-order-cta" className="btn-primary">
                {t("Order Your Song")} <ArrowRight size={16} />
              </Link>
              <button
                data-testid="home-hero-listen-button"
                onClick={() => playTrack(0)}
                className="btn-ghost"
              >
                <Play size={16} /> {t("Listen to Our Work")}
              </button>
            </div>
            <div className="mt-10 flex flex-wrap gap-6">
              {[
                ["500+", "Songs produced"],
                ["24h", "Express delivery"],
                ["3", "Styles per package"],
              ].map(([n, l]) => (
                <div key={l}>
                  <p className="font-display text-2xl font-bold text-[#FFC857]">{n}</p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-slate-500">{t(l)}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="fade-up relative" style={{ animationDelay: "0.15s" }}>
            <div className="studio-card overflow-hidden">
              <img src={HERO_IMG} alt={t("Evi-Studio mixing console during a recording session")} className="h-80 w-full object-cover sm:h-96" />
              <div className="flex items-center justify-between border-t border-[#282C3D] px-5 py-4">
                <div>
                  <p className="text-sm font-semibold text-slate-100">{t("Now playing in the studio")}</p>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{t("Live from the sidebar player")}</p>
                </div>
                <Eq playing={isPlaying} />
              </div>
            </div>
          </div>
        </div>
      </section>
  );
};

export const HomeProcess = () => {
  const { t } = useLanguage();
  return (
      <section className="border-y border-[#282C3D] bg-[#0E1018]">
        <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6 lg:px-8">
          <p className="eyebrow">{t("How it works")}</p>
          <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-100 sm:text-3xl lg:text-4xl">
            {t("From your idea to a finished song")}
          </h2>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s, i) => (
              <div key={s.title} data-testid={`home-step-${i + 1}`} className="studio-card fade-up p-6" style={{ animationDelay: `${i * 0.08}s` }}>
                <s.icon className="text-[#E5A93C]" size={22} />
                <h3 className="mt-4 font-display text-lg font-semibold text-slate-100">{t(s.title)}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">{t(s.text)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
  );
};

export const HomePackages = () => {
  const { t } = useLanguage();
  return (
      <section className="mx-auto max-w-6xl px-4 py-20 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="eyebrow">{t("Offers")}</p>
            <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-100 sm:text-3xl lg:text-4xl">
              {t("One studio. Four ways to sound.")}
            </h2>
          </div>
          <Link to="/order" data-testid="home-packages-order-link" className="btn-ghost">
            {t("Start an Order")} <ArrowRight size={16} />
          </Link>
        </div>
        <div className="mt-10 grid gap-6 md:grid-cols-2">
          {PACKAGES.map((p, i) => (
            <div key={p.name} data-testid={p.testid} className="studio-card fade-up flex flex-col p-6" style={{ animationDelay: `${i * 0.08}s` }}>
              <div className="flex items-center justify-between gap-3">
                <p.icon className="text-[#E5A93C]" size={22} />
                <span className="rounded-full border border-[#282C3D] px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-slate-400">
                  {t(p.tag)}
                </span>
              </div>
              <h3 className="mt-5 font-display text-xl font-semibold text-slate-100">{t(p.name)}</h3>
              <p className="mt-1 font-display text-2xl font-bold text-[#FFC857]">{t(p.price)}</p>
              <ul className="mt-4 flex-1 space-y-2.5 text-sm text-slate-400">
                {p.points.map((pt) => (
                  <li key={pt} className="flex gap-2">
                    <CheckCircle2 size={15} className="mt-0.5 shrink-0 text-[#10B981]" />
                    {t(pt)}
                  </li>
                ))}
              </ul>
              <Link to={p.to} data-testid={`${p.testid}-cta`} className="btn-ghost mt-6 w-full !px-4">
                {t(p.cta)}
              </Link>
            </div>
          ))}
        </div>
      </section>
  );
};

export const HomeOrderCta = () => {
  const { t } = useLanguage();
  return (
      <section className="mx-auto max-w-6xl px-4 pb-24 sm:px-6 lg:px-8">
        <div className="studio-card flex flex-col items-start justify-between gap-6 border-[#E5A93C]/30 bg-[#1A1D2B] p-8 sm:p-12 md:flex-row md:items-center">
          <div>
            <p className="eyebrow">{t("Ready when you are")}</p>
            <h2 className="mt-2 font-display text-2xl font-bold text-slate-50 sm:text-3xl">
              {t("A song nobody else in the world has.")}
            </h2>
            <p className="mt-2 max-w-xl text-sm text-slate-400">
              {t("Request today — lyrics first, your confirmation, instant online payment, then straight into production.")}
            </p>
          </div>
          <Link to="/order" data-testid="home-bottom-order-cta" className="btn-primary shrink-0">
            {t("Order Your Song")} <ArrowRight size={16} />
          </Link>
        </div>
      </section>
  );
};

export default function Home() {
  return <div><HomeHero /><HomeProcess /><HomePackages /><HomeOrderCta /></div>;
}
